import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, type RefObject, useEffect, useEffectEvent, useId, useLayoutEffect, useRef, useState } from "react";

import { ApiError } from "../../api/client";
import type { ActivityDirection, ClientAssignment, ClientChoice, DismissReason, Submission } from "../../api/types";
import { useSession } from "../../app/session-context";
import { Badge, HipaaBadge } from "../../components/ui/Badge";
import { ChevronLeftIcon, ClockIcon, CopyIcon, EmailIcon, ExternalIcon } from "../../components/ui/Icons";
import { MenuButton } from "../../components/ui/Menu";
import { ServiceTile, SourceTile } from "../../components/ui/SourceTile";
import { MENU_SEPARATOR, type MenuEntry } from "../../components/ui/useMenu";
import { formatClockRange } from "../../lib/dates";
import { formatDuration } from "../../lib/durations";
import { networkRouteOf, networkServiceOf } from "../../lib/networkServices";
import { usePrivacy } from "../../lib/privacy";
import { laneOf, sourceNames } from "../../lib/sources";
import { useAnnounce } from "./announce";
import { AssignClient } from "./AssignClient";
import { ChangeDuration } from "./ChangeDuration";
import { DismissBlock, DismissedBlock } from "./DismissBlock";
import { type LogStart, LogToAutotask } from "./LogToAutotask";
import { MergeBlock, SplitBlock } from "./MergeSplit";
import { MoveAnActivity } from "./MoveActivity";
import type { TicketCardView } from "./board";
import {
  type BlockView,
  DISMISS_REASONS,
  type PopupPane,
  activityRange,
  activityTitle,
  blockSummary,
  inProgressNote,
  mergeable,
  popupPanes,
  readyForTicket,
  stillGoing,
} from "./model";
import { OnThisTicket } from "./TicketCard";
import { canClose, ticketName } from "./tickets";
import { VendorPane } from "./VendorPane";

const DIRECTIONS: Partial<Record<ActivityDirection, string>> = { inbound: "Inbound", outbound: "Outbound" };

/** Each pane's heading. */
const PANE_TITLES: Record<PopupPane, string> = {
  client: "Change client",
  duration: "Change the duration",
  move: "Move an activity",
  merge: "Merge with another block",
  split: "Split this block",
  dismiss: "Dismiss this block",
  vendor: "Vendor",
};

/**
 * Where a block's popup starts (ADR 0194): on a pane, or with the Log form's
 * Ticket list open, as a card's right-click menu asks. Only for its block.
 */
export interface PopupStart {
  blockId: string;
  pane?: PopupPane | undefined;
  /** The Dismiss pane's reason, chosen already (Other…): its note takes focus. */
  reason?: DismissReason | undefined;
  /** The Log form's Ticket list open, on its search ("Add to existing ticket…"). */
  ticketListOpen?: boolean | undefined;
  /** The primary form's first field focused, once it's there ("Log…", "Assign client…"). */
  focusForm?: boolean | undefined;
  /** Close ticket asking already ("Close ticket…"). */
  closeTicket?: boolean | undefined;
}

/** What opening another block from this popup can ask for (`DayPage`'s `openBlock`). */
export interface OpenOptions {
  pane?: PopupPane | undefined;
  reason?: DismissReason | undefined;
  ticketListOpen?: boolean | undefined;
  focusForm?: boolean | undefined;
  closeTicket?: boolean | undefined;
  /** Its Log form on this ticket, as a drop on the ticket's card (ADR 0187). */
  logStart?: LogStart | undefined;
}

// The primary form's first field: the Log form's Ticket, or the client chooser.
const FIRST_FIELD = "form :is(input, select, textarea, button)";

/** What the right pane is for, first: its heading, and what Back goes back to. */
function primaryName(block: BlockView): string {
  switch (block.kind) {
    case "needs_client":
      return "Assign a client";
    case "dismissed":
      return "Dismissed";
    case "logged":
      return "In Autotask";
    default:
      return "Log to Autotask";
  }
}

/** Whether a box scrolls: its content is taller than it. */
function useOverflows(ref: RefObject<HTMLElement | null>, content: unknown): boolean {
  const [overflows, setOverflows] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (element === null || typeof ResizeObserver === "undefined") {
      return;
    }
    const measure = () => {
      setOverflows(element.scrollHeight > element.clientHeight + 1);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    measure();
    return () => {
      observer.disconnect();
    };
  }, [ref, content]);
  return overflows;
}

function refusal(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return "You can only change your own blocks.";
    }
    if (error.status === 409) {
      return error.detail === null ? "This block can't change any more. Reload to see where it is now." : `Couldn't do that: ${error.detail}.`;
    }
    return error.fieldErrors[0]?.message ?? error.detail ?? error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong.";
}

/** The browser's clipboard, where it has one. */
function clipboard(): Clipboard | undefined {
  return (navigator as { clipboard?: Clipboard }).clipboard;
}

const actionButton =
  "inline-flex h-[34px] cursor-pointer items-center gap-1.5 rounded-pill border pr-3 pl-3.5 text-[13px] font-semibold whitespace-nowrap ";
const dismissButton =
  actionButton +
  "border-status-needs-client-line bg-panel text-status-needs-client-fg hover:border-status-needs-client-fg hover:bg-status-needs-client-bg " +
  "aria-expanded:border-status-needs-client-fg aria-expanded:bg-status-needs-client-bg";
const moreButton =
  actionButton +
  "border-line bg-panel text-text hover:border-brand hover:bg-panel-2 " +
  "aria-expanded:border-brand aria-expanded:bg-brand-tint aria-expanded:text-brand-ink-on-tint";

/**
 * A block's details (section 11.0), in its popup (BlockPopup; ADRs 0184,
 * 0193 and 0194), in two panes side by side from 860 px wide, stacked below
 * that:
 *
 * - **The block** (left): its billing and state, client, duration, times,
 *   activity count, sources and badges, then all its activities in compact
 *   rows. A logged block whose ticket has other time entries that day lists
 *   them, "On this ticket today" (ADR 0189).
 * - **What to do with it** (right): the primary action first. A block with
 *   no client offers to choose one (ADR 0033); a ready one, to log it to
 *   Autotask (section 10.1); a logged one says what was logged, with Close
 *   ticket while it's open; a dismissed one, why, with Restore (ADR 0058).
 * - **Dismiss ▾ and More ⋯**, under it (ADR 0194 decision 2): two menus.
 *   - Dismiss ▾ has the reasons: one dismisses the block, and the popup
 *     closes with the next card focused. Other… opens the Dismiss pane.
 *   - More ⋯ has the rest: Change client…, Duration…, Move an activity…,
 *     Split…, Merge with ▸, Vendor… (Needs you), Copy summary; a logged
 *     block's, Open ticket in Autotask, Copy ticket number, Log a Ready block
 *     here ▸ (while it's open) and Copy summary.
 *   - An item with "…" replaces the right pane with its form under a
 *     heading, with a Back link. Back, or Cancel, goes back with focus on the
 *     menu's button; once it's done, the heading takes focus and the day view
 *     says what happened (#247).
 *
 * Keyed by block, so switching blocks starts afresh, on the primary pane
 * unless `start` says otherwise.
 */
export function BlockDetails({
  block,
  timeZone,
  targets = [],
  onSelect,
  onOpenBlock,
  onClose,
  headingRef,
  onLogged,
  onDismissed,
  logStart,
  start,
  ticketCard,
}: {
  block: BlockView;
  /** Its ticket's card, when it shares one with the ticket's other time entries that day (ADR 0189). */
  ticketCard?: TicketCardView | null | undefined;
  timeZone: string;
  /** Dropped on an open ticket: its Log form starts there (v0.9.0's P4). */
  logStart?: LogStart | null | undefined;
  /** Where the popup starts: a pane, or the Ticket list open (ADR 0194). */
  start?: PopupStart | null | undefined;
  /** The day's other blocks still to log: what an activity can move to (ADR 0060), and merge with. */
  targets?: readonly BlockView[];
  /** Shows another block in the popup, as a merge does (#248). */
  onSelect?: ((blockId: string) => void) | undefined;
  /** Shows another block in the popup, on a pane or its Log form on a ticket ("Log a Ready block here"). */
  onOpenBlock?: ((blockId: string, options: OpenOptions) => void) | undefined;
  /** Closes the popup: once a block is dismissed from its menu, or marked a vendor's. */
  onClose?: (() => void) | undefined;
  /** The block's heading: the popup's name, and where focus goes (Idea #287). */
  headingRef?: RefObject<HTMLHeadingElement | null>;
  /** Logged from here (ADR 0184): its ticket, as techs know it. */
  onLogged?: ((ticket: string | null) => void) | undefined;
  /** Dismissed from Dismiss ▾ here: the board offers Undo (Discussion #877). */
  onDismissed?: ((block: BlockView, reason: DismissReason) => void) | undefined;
}) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const privacy = usePrivacy();
  const startHere = start?.blockId === block.id ? start : undefined;
  const [pane, setPane] = useState<PopupPane | null>(startHere?.pane ?? null);
  const [reason, setReason] = useState<DismissReason | undefined>(startHere?.reason);
  const [notice, setNotice] = useState<string | null>(null);
  // What Copy put on the clipboard, or couldn't.
  const [copied, setCopied] = useState<{ text: string; ok: boolean } | null>(null);
  // The Log form's actions, kept at the foot of the right pane while it
  // scrolls on its own (#222): their height, as it changes.
  const [actionsHeight, setActionsHeight] = useState(0);
  const details = useRef<HTMLDivElement>(null);
  const actionPane = useRef<HTMLElement>(null);
  const activityList = useRef<HTMLUListElement>(null);
  const back = useRef<HTMLButtonElement>(null);
  const dismissMenu = useRef<HTMLButtonElement>(null);
  const moreMenu = useRef<HTMLButtonElement>(null);
  const copyField = useRef<HTMLInputElement>(null);
  // Back or Cancel: the menu's button that led to the pane takes focus again.
  const backTo = useRef<"dismiss" | "more" | null>(null);
  // A pane that focuses a field of its own (Other…'s note), not Back.
  const ownFocus = useRef(startHere?.reason !== undefined);
  const activitiesId = useId();
  // After an action, its form or button is gone: focus moves to the heading,
  // not to the page, and the day view says what happened (#247). Only from
  // inside the details, or from nowhere: never from where the tech has gone.
  // The popup's, when it passes one (Idea #287): after a merge moves the
  // popup to the kept block, its heading is the one that takes focus.
  const ownHeading = useRef<HTMLHeadingElement>(null);
  const heading = headingRef ?? ownHeading;
  const announce = useAnnounce();
  const done = (message?: string) => {
    if (message !== undefined) {
      announce(message);
    }
    const focused = document.activeElement;
    if (focused === null || focused === document.body || details.current?.contains(focused)) {
      heading.current?.focus();
    }
  };
  /** An action done: back to the primary pane, the heading focused, and it's said. */
  const finish = (message?: string) => {
    backTo.current = null;
    setPane(null);
    done(message);
  };
  /** Back, or Cancel: the primary pane again, focus on the menu's button that led there. */
  const goBack = () => {
    backTo.current = pane === "dismiss" ? "dismiss" : "more";
    setPane(null);
  };
  /** A menu's "…" item: its pane, in place of the primary action. */
  const openPane = (next: PopupPane, chosen?: DismissReason) => {
    if (next === "client") {
      setNotice(null);
    }
    setCopied(null);
    setReason(chosen);
    ownFocus.current = chosen !== undefined;
    setPane(next);
  };
  const assigned = (result: ClientAssignment, client: ClientChoice) => {
    const parts = [`Assigned to ${client.name}.`];
    if (result.remembered !== null && block.number) {
      parts.push(`Calls from ${block.number} will match ${client.name} from now on.`);
    }
    if (result.also_matched > 0) {
      const calls = result.also_matched === 1 ? "call" : "calls";
      parts.push(`${String(result.also_matched)} more ${calls} from it today matched too.`);
    }
    setNotice(parts.join(" "));
    // The notice says it.
    finish();
  };
  // Dismissed from Dismiss ▾ or Vendor…: the popup closes (ADR 0194). As
  // the block leaves the board, the day view closes it, and the next card
  // takes focus; shown still (Show dismissed), it's closed here once the day
  // shows it dismissed.
  const closeWhenDismissed = useRef(false);
  const gone = (message: string) => {
    announce(message);
    closeWhenDismissed.current = true;
  };
  const closeNow = useEffectEvent(() => {
    onClose?.();
  });
  useEffect(() => {
    if (closeWhenDismissed.current && block.kind === "dismissed") {
      closeWhenDismissed.current = false;
      closeNow();
    }
  }, [block.kind]);
  // A reason from Dismiss ▾: one click, and the block is dismissed (ADR 0194).
  const dismissNow = useMutation({
    mutationFn: (chosen: DismissReason) => api.dismissBlock(block.id, chosen, null),
    onSuccess: async (_, chosen) => {
      await queryClient.invalidateQueries({ queryKey: ["day"] });
      gone(`Dismissed ${block.title}.`);
      onDismissed?.(block, chosen);
    },
  });
  // A block from Merge with ▸: one click, as a drag onto its card (ADR 0118).
  const mergeNow = useMutation({
    mutationFn: (other: string) => api.mergeBlocks(block.id, other),
    onSuccess: async (merged) => {
      if (merged.removed_block_id === block.id) {
        onSelect?.(merged.block_id);
      }
      await queryClient.invalidateQueries({ queryKey: ["day"] });
      done("Merged into one block.");
      // The Log form's preview is built from the block's activities (#224).
      await queryClient.invalidateQueries({ queryKey: ["submission-preview"] });
    },
  });
  const failure = dismissNow.error ?? mergeNow.error;
  /** Copied for real, inside the click or key that asked for it; the browser may still refuse. */
  const copy = (text: string) => {
    let written: Promise<void> | null;
    try {
      written = clipboard()?.writeText(text) ?? null;
    } catch {
      written = null;
    }
    if (written === null) {
      setCopied({ text, ok: false });
      return;
    }
    written.then(
      () => {
        setCopied({ text, ok: true });
      },
      () => {
        setCopied({ text, ok: false });
      },
    );
  };
  const needsClient = block.kind === "needs_client";
  const dismissed = block.kind === "dismissed";
  const panes = popupPanes(block, targets, timeZone);
  // A pane that can't be used any more (the block changed under it): the
  // primary pane again, for good.
  const shown = pane !== null && panes.includes(pane) ? pane : null;
  if (pane !== null && shown === null) {
    setPane(null);
  }
  const primary = primaryName(block);
  const long = block.activities.length > 3;
  const listScrolls = useOverflows(activityList, block.activities.length);

  // A pane opened: focus on its Back link, unless the pane takes it itself.
  // Back to the primary pane: on the menu's button that led there.
  useLayoutEffect(() => {
    if (shown !== null) {
      if (!ownFocus.current) {
        back.current?.focus();
      }
    } else if (backTo.current !== null) {
      const button = backTo.current === "dismiss" ? dismissMenu.current : moreMenu.current;
      backTo.current = null;
      (button ?? heading.current)?.focus();
    }
  }, [shown, heading]);
  // "Log…" or "Assign client…" from a card's menu (ADR 0194): the form's
  // first field takes focus once it's on the page and can take it (the Log
  // form waits for the block's submission to be read, the client chooser
  // for the clients), unless the tech has gone elsewhere.
  const fieldWanted = useRef(startHere?.focusForm === true);
  useEffect(() => {
    const host = actionPane.current;
    if (!fieldWanted.current || host === null) {
      return;
    }
    const focusField = (): boolean => {
      const field = host.querySelector<HTMLElement>(FIRST_FIELD);
      if (field === null || field.matches(":disabled")) {
        return false;
      }
      fieldWanted.current = false;
      // Focus still where the popup opened from (the card behind it), on
      // nothing, or on the heading it starts on: the tech hasn't moved it.
      const focused = document.activeElement;
      const popup = host.closest('[role="dialog"]');
      if (
        focused === null ||
        focused === document.body ||
        focused === heading.current ||
        (popup !== null && !popup.contains(focused))
      ) {
        field.focus();
      }
      return true;
    };
    if (focusField() || typeof MutationObserver === "undefined") {
      return;
    }
    const observer = new MutationObserver(() => {
      if (focusField()) {
        observer.disconnect();
      }
    });
    observer.observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ["disabled"] });
    return () => {
      observer.disconnect();
    };
  }, [heading]);
  // Copying refused: the text, selected, to copy by hand.
  useLayoutEffect(() => {
    if (copied !== null && !copied.ok) {
      copyField.current?.focus();
      copyField.current?.select();
    }
  }, [copied]);

  // ---- The menus (ADR 0194 decision 2) ----
  const paneItem = (target: PopupPane, label: string, icon?: ReactNode): MenuEntry => ({
    label,
    icon,
    onSelect: () => {
      openPane(target);
    },
  });
  const copySummary: MenuEntry = {
    label: "Copy summary",
    icon: <CopyIcon />,
    onSelect: () => {
      copy(blockSummary(block, timeZone));
    },
  };
  const dismissItems = (): MenuEntry[] => [
    ...DISMISS_REASONS.filter((r) => r.reason !== "other").map(
      (r): MenuEntry => ({
        label: r.label,
        warn: true,
        onSelect: () => {
          dismissNow.mutate(r.reason);
        },
      }),
    ),
    MENU_SEPARATOR,
    {
      label: "Other…",
      onSelect: () => {
        openPane("dismiss", "other");
      },
    },
  ];
  const mergeItems = (): MenuEntry[] => [
    ...mergeable(block, targets).map(
      (other): MenuEntry => ({
        label: `${other.title} · ${formatClockRange(other.startedAt, other.endedAt, timeZone)}`,
        hint: other.duration,
        onSelect: () => {
          mergeNow.mutate(other.id);
        },
      }),
    ),
    MENU_SEPARATOR,
    {
      label: "Choose a block…",
      onSelect: () => {
        openPane("merge");
      },
    },
  ];
  const ticket = block.kind === "logged" ? block.ticket : null;
  const ticketItems = (): MenuEntry[] => {
    if (ticket === null) {
      return [];
    }
    const name = ticketName(ticket);
    // Autotask's own link (ADR 0066); none in privacy mode (ADR 0145).
    const url = privacy
      ? null
      : (queryClient.getQueryData<Submission | null>(["submission", block.id])?.ticket_url ?? null);
    return [
      ...(url === null
        ? []
        : [
            {
              label: `Open ticket ${name} in Autotask`,
              icon: <ExternalIcon />,
              onSelect: () => {
                window.open(url, "_blank", "noopener,noreferrer");
              },
            },
          ]),
      {
        label: "Copy ticket number",
        icon: <CopyIcon />,
        onSelect: () => {
          copy(name);
        },
      },
    ];
  };
  const logHereItems = (): MenuEntry[] => {
    const ready = readyForTicket(block, targets);
    if (ready.length === 0 || ticket === null) {
      return [{ label: "No Ready blocks for this client", disabled: true }];
    }
    return ready.map(
      (other): MenuEntry => ({
        label: formatClockRange(other.startedAt, other.endedAt, timeZone),
        hint: other.duration,
        onSelect: () => {
          onOpenBlock?.(other.id, {
            logStart: { blockId: other.id, ticketId: ticket.ticketId, number: ticket.number, closed: ticket.state === "closed" },
          });
        },
      }),
    );
  };
  const moreItems = (): MenuEntry[] => {
    if (ticket !== null) {
      return [
        ...ticketItems(),
        MENU_SEPARATOR,
        ...(canClose(ticket.state) && onOpenBlock !== undefined
          ? [{ label: "Log a Ready block here", items: logHereItems }, MENU_SEPARATOR]
          : []),
        copySummary,
      ];
    }
    return [
      ...(panes.includes("client") ? [paneItem("client", "Change client…")] : []),
      ...(panes.includes("duration") ? [paneItem("duration", "Duration…", <ClockIcon />)] : []),
      ...(panes.includes("move") ? [paneItem("move", "Move an activity…")] : []),
      ...(panes.includes("split") ? [paneItem("split", "Split…")] : []),
      ...(panes.includes("merge") ? [{ label: "Merge with", items: mergeItems }] : []),
      ...(panes.includes("vendor") ? [MENU_SEPARATOR, paneItem("vendor", "Vendor…", <EmailIcon />)] : []),
      MENU_SEPARATOR,
      copySummary,
    ];
  };
  // Dismiss ▾ (a block still to log) and More ⋯, under the primary action.
  const menus = (
    <div data-part="popup-actions" className="relative flex flex-wrap items-center gap-2">
      {block.canDismiss ? (
        <MenuButton ref={dismissMenu} menuLabel="Dismiss as" items={dismissItems} className={dismissButton}>
          Dismiss
          <span aria-hidden="true" className="text-[11px]">
            ▾
          </span>
        </MenuButton>
      ) : null}
      <MenuButton ref={moreMenu} menuLabel="More actions" items={moreItems} className={moreButton}>
        More
        <span aria-hidden="true" className="text-[11px] text-muted group-aria-expanded:text-current">
          ⋯
        </span>
      </MenuButton>
    </div>
  );

  const activities = block.activities.map((activity) => {
    const lane = laneOf(activity.source);
    // A network activity's service: its own icon and name (ADR 0165).
    const service = networkServiceOf(activity);
    // Over the VPN, it says so; office work looks as it always did (ADR 0180).
    const route = networkRouteOf(activity);
    const direction = DIRECTIONS[activity.direction];
    // An email moved in from an earlier day by its thread says its day (ADR 0131).
    const range = activityRange(activity, block, timeZone);
    const title = activityTitle(activity);
    return (
      <li
        key={activity.id}
        className="grid grid-cols-[26px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-row bg-panel-2 py-[5px] pr-3 pl-2"
      >
        {service ? <ServiceTile service={service} /> : lane ? <SourceTile lane={lane} /> : <span />}
        <span className="flex min-w-0 flex-col gap-px">
          {/* Cut short on a wide screen, whole on hover; a phone wraps it. */}
          <span
            title={title}
            className="truncate text-[13px] font-semibold max-[639px]:overflow-visible max-[639px]:whitespace-normal max-[639px]:wrap-anywhere"
          >
            {title}
          </span>
          <span className="font-mono text-[11px] text-muted">
            {[
              range,
              // The one keeping the block in progress (Discussion #878).
              ...(stillGoing(activity, block) ? ["still going"] : []),
              ...(direction && activity.summary.trim() !== "" ? [direction] : []),
              ...(route ? [route] : []),
            ].join(" · ")}
          </span>
        </span>
        <span className="text-sm font-semibold tracking-[-0.02em] whitespace-nowrap">
          {formatDuration(activity.counted_seconds)}
        </span>
      </li>
    );
  });

  const count = block.activities.length;
  const meta = [
    formatClockRange(block.startedAt, block.endedAt, timeZone),
    `${String(count)} ${count === 1 ? "activity" : "activities"}`,
    ...(block.lanes.length > 0 ? [sourceNames(block.lanes)] : []),
  ];

  // The primary action, then the menus: the Log form keeps them in its
  // place, beside Close ticket while there's one (LogToAutotask).
  const primaryAction = dismissed ? (
    <>
      <DismissedBlock block={block} onDone={done} />
      {menus}
    </>
  ) : needsClient ? (
    <>
      <AssignClient block={block} onAssigned={assigned} />
      {menus}
    </>
  ) : block.kind === "in_progress" ? (
    <>
      <p className="rounded-row border border-dashed border-line px-3 py-2.5 text-[13px] text-muted">
        {inProgressNote(block, timeZone)}
      </p>
      {menus}
    </>
  ) : (
    <LogToAutotask
      block={block}
      timeZone={timeZone}
      start={logStart}
      ticketListOpen={startHere?.ticketListOpen === true}
      closeAsking={startHere?.closeTicket === true}
      onActionsResize={setActionsHeight}
      onSent={done}
      onLogged={onLogged}
      actions={menus}
    />
  );

  return (
    // Side by side from 860 px wide, each pane scrolling on its own if the
    // window is too short; stacked below that, scrolling as one.
    <div
      ref={details}
      data-part="block-details"
      className={
        "flex min-h-0 flex-auto flex-col overflow-y-auto " +
        "min-[860px]:grid min-[860px]:grid-cols-[minmax(0,1.06fr)_minmax(0,1fr)] min-[860px]:grid-rows-[minmax(0,1fr)] min-[860px]:overflow-visible"
      }
    >
      <section
        aria-label="The block"
        data-part="block-pane"
        className={
          "flex min-h-0 shrink-0 flex-col gap-2.5 border-b border-line px-[22px] pt-[18px] pb-5 " +
          "min-[860px]:overflow-y-auto min-[860px]:border-r min-[860px]:border-b-0 max-[639px]:p-4"
        }
      >
        <div className="flex shrink-0 items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-[3px]">
            <span className="text-[12.5px] font-[650] tracking-[0.08em] text-brand-ink uppercase">
              {block.heading}
            </span>
            {/* The popup's name (aria-labelledby), and where focus goes on opening. */}
            <Dialog.Title
              ref={heading}
              tabIndex={-1}
              className="text-[24px] leading-[1.2] font-[650] tracking-[-0.03em] text-balance wrap-anywhere max-[639px]:text-[22px]"
            >
              {block.title}
            </Dialog.Title>
          </div>
          <span className="text-[40px] leading-none font-semibold tracking-[-0.05em] whitespace-nowrap max-[639px]:text-[34px]">
            {block.duration}
          </span>
        </div>
        <p className="shrink-0 text-[12.5px] leading-[1.45] text-muted">
          <span className="font-mono text-xs">{meta[0]}</span>
          {` · ${meta.slice(1).join(" · ")}`}
        </p>
        {block.isHipaa || block.overlapsOtherClient || block.continuesAnother || block.overridden ? (
          <div className="flex shrink-0 flex-wrap gap-1.5">
            {block.isHipaa ? <HipaaBadge /> : null}
            {block.overridden ? (
              <Badge tone="neutral">{`Your duration · counted ${formatDuration(block.countedSeconds)}`}</Badge>
            ) : null}
            {block.overlapsOtherClient ? <Badge tone="neutral">Overlaps another client</Badge> : null}
            {block.continuesAnother ? <Badge tone="neutral">Continues a logged block</Badge> : null}
          </div>
        ) : null}
        {count > 0 ? (
          <>
            <div className="flex shrink-0 items-baseline justify-between gap-2 text-muted">
              <span id={activitiesId} className="text-[11.5px] font-[650] tracking-[0.08em] uppercase">
                Activities
              </span>
              <span className="text-xs">{long ? `All ${String(count)}, in time order` : "In time order"}</span>
            </div>
            {/* A long list gives way first, from 860 px wide: then only it
                scrolls, in its own box, which Tab reaches while it does. */}
            <ul
              ref={activityList}
              aria-labelledby={activitiesId}
              tabIndex={listScrolls ? 0 : undefined}
              data-part="activity-list"
              className={
                "flex shrink-0 flex-col gap-1 " +
                (long ? "min-[860px]:min-h-[104px] min-[860px]:shrink min-[860px]:overflow-y-auto" : "")
              }
            >
              {activities}
            </ul>
          </>
        ) : null}
        {ticketCard ? (
          <div className="shrink-0">
            <OnThisTicket card={ticketCard} blockId={block.id} timeZone={timeZone} onSelect={onSelect} />
          </div>
        ) : null}
      </section>
      <section
        ref={actionPane}
        aria-label="What to do with it"
        data-part="action-pane"
        // Its scroll padding keeps a field the tech tabs to clear of the Log
        // form's actions, which stay at its foot while it scrolls, however
        // tall they are (#222): their height, the 18 px bottom padding they
        // sit above, and a 10 px gap.
        style={actionsHeight > 0 ? { scrollPaddingBottom: `${String(actionsHeight + 18 + 10)}px` } : undefined}
        className={
          "flex min-h-0 shrink-0 flex-col gap-2.5 px-[22px] py-[18px] *:shrink-0 " +
          "min-[860px]:overflow-y-auto max-[639px]:p-4"
        }
      >
        {shown === null ? (
          <>
            <h3 className="text-[11.5px] font-[650] tracking-[0.08em] text-muted uppercase">{primary}</h3>
            {notice ? (
              <p role="status" className="rounded-row bg-status-logged-bg px-3 py-2.5 text-[13px] text-status-logged-fg">
                {notice}
              </p>
            ) : null}
            {primaryAction}
            {failure === null ? null : (
              <p role="alert" className="text-[13px] text-status-needs-client-fg">
                {refusal(failure)}
              </p>
            )}
            {copied === null ? null : copied.ok ? (
              <p role="status" className="truncate text-[12.5px] text-muted">
                {`Copied: ${copied.text}`}
              </p>
            ) : (
              <div role="alert" className="flex flex-col gap-1.5 text-[12.5px] text-status-needs-client-fg">
                <label htmlFor={`${activitiesId}-copy`}>Copying was blocked here. Select it to copy it by hand:</label>
                <input
                  ref={copyField}
                  id={`${activitiesId}-copy`}
                  readOnly
                  value={copied.text}
                  className="h-9 w-full rounded-input border border-line bg-card px-3 font-mono text-[12px] text-text"
                />
              </div>
            )}
          </>
        ) : (
          <>
            <button
              ref={back}
              type="button"
              onClick={goBack}
              className="inline-flex cursor-pointer items-center gap-1 self-start rounded-md py-0.5 pr-1 text-[13px] font-[650] text-brand-ink hover:underline"
            >
              <ChevronLeftIcon />
              <span>{`Back to ${primary}`}</span>
            </button>
            <h3 className="text-[17px] font-[650] tracking-[-0.015em]">{PANE_TITLES[shown]}</h3>
            {shown === "client" ? (
              <>
                <AssignClient block={block} onAssigned={assigned} />
                <button
                  type="button"
                  onClick={goBack}
                  className="h-10 cursor-pointer self-start rounded-pill border border-line bg-panel px-4 text-[13.5px] font-semibold hover:bg-panel-2"
                >
                  Cancel
                </button>
              </>
            ) : shown === "duration" ? (
              <ChangeDuration block={block} onDone={finish} onCancel={goBack} />
            ) : shown === "move" ? (
              <MoveAnActivity
                block={block}
                targets={targets}
                timeZone={timeZone}
                onDone={(message) => {
                  if (message === undefined) {
                    goBack();
                  } else {
                    finish(message);
                  }
                }}
              />
            ) : shown === "merge" ? (
              <MergeBlock
                block={block}
                targets={targets}
                timeZone={timeZone}
                onDone={finish}
                onCancel={goBack}
                onSelect={onSelect}
              />
            ) : shown === "split" ? (
              <SplitBlock block={block} timeZone={timeZone} onDone={finish} onCancel={goBack} />
            ) : shown === "dismiss" ? (
              <DismissBlock block={block} onDone={finish} onCancel={goBack} initialReason={reason} />
            ) : (
              <VendorPane block={block} onDone={gone} onCancel={goBack} />
            )}
          </>
        )}
      </section>
    </div>
  );
}
